import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { styled, keyframes, alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import logo from '../../assets/Logo.png';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const floatingBubbles = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
  100% { transform: translateY(0) scale(1); }
`;

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  background: 'linear-gradient(-45deg, #60a5fa, #34d399, #3b82f6, #f472b6)',
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',

  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: `${floatingBubbles} 6s infinite ease-in-out`,
    [theme.breakpoints.down('sm')]: {
      width: '300px',
      height: '300px',
    }
  },

  '&::before': {
    top: '-100px',
    left: '-100px',
    animationDelay: '0s',
  },

  '&::after': {
    bottom: '-100px',
    right: '-100px',
    animationDelay: '-3s',
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  position: 'relative',
  zIndex: 1,
  overflow: 'visible',
  marginTop: theme.spacing(4),

  // Text and input colors for dark mode
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },

  '& .MuiFormHelperText-root': {
    color: theme.palette.text.secondary,
  },

  '& .MuiSelect-select': {
    color: theme.palette.text.primary,
  },

  '& .MuiCheckbox-root': {
    color: theme.palette.text.secondary,
  },

  [theme.breakpoints.up('lg')]: { maxWidth: 520 },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '90%',
    margin: theme.spacing(3, 2, 2, 2),
  }
}));

const Logo = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%) translateY(-50%)',
  top: -8,
  zIndex: 2,
  transition: 'transform 0.3s ease',
  '&:hover': { transform: 'translateX(-50%) translateY(-52%)' },
  [theme.breakpoints.down('sm')]: { width: 60, height: 60 }
}));

const PasswordStrengthBar = styled(LinearProgress)(({ strength }) => ({
  marginTop: 8,
  height: 4,
  borderRadius: 2,
  backgroundColor: 'rgba(0,0,0,0.1)',
  '& .MuiLinearProgress-bar': {
    backgroundColor:
      strength === 'weak' ? '#ef4444' :
      strength === 'medium' ? '#f59e0b' :
      strength === 'strong' ? '#10b981' : '#e5e7eb',
  }
}));

const Registered = () => {
  const theme = useTheme(); // Add this line
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    country: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const countries = [
    'Egypt',
    'United States',
    'United Kingdom',
    'Australia',
    'Germany',
    'France',
    'Other'
  ];

  const phonePlaceholders = {
    "Egypt": "+20 1X XXX XXXX",
    "United States": "+1 (123) 456-7890",
    "United Kingdom": "+44 7XXX XXXXXX",
    "Australia": "+61 4XX XXX XXX",
    "Germany": "+49 1512 3456789",
    "France": "+33 6 12 34 56 78",
    "Other": "Enter your phone number"
  };

  const checkPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 8) return 'weak';
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecial = /[^a-zA-Z0-9]/.test(password);
    if (hasLetters && hasNumbers && hasSpecial && password.length >= 12) return 'strong';
    if ((hasLetters && hasNumbers) || (hasLetters && hasSpecial) || (hasNumbers && hasSpecial)) return 'medium';
    return 'weak';
  };

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, password: value });
    setPasswordStrength(checkPasswordStrength(value));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength === 'weak') {
      newErrors.password = 'Password is too weak (include letters, numbers & special chars)';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Phone is required by backend (`phone_number`)
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.country) {
      newErrors.country = 'Please select a country';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show only the first error to avoid toast spam
      const firstError = Object.values(errors)[0];
      if (firstError) toast.error(firstError);
      return;
    }

    setIsLoading(true);

    try {
      const { email, password, confirmPassword, full_name, phone, country } = formData;
      const apiUrl = process.env.REACT_APP_API_URL;

      // IMPORTANT: match backend keys exactly
      const payload = {
        full_name,
        email,
        password,
        password_confirmation: confirmPassword,
        phone_number: phone,
        country
      };

      // Endpoint fixed to /auth/register
      const response = await axios.post(`${apiUrl}/auth/register`, payload);

      if (response.data?.success) {
        const { token, user } = response.data.data || {};

        // Clean success toast (✅ icon)
        toast.success('Registration successful', {
          icon: '✅',
          theme: 'colored',
          autoClose: 2000
        });

        if (token) localStorage.setItem('token', token);
        if (user) localStorage.setItem('user', JSON.stringify(user));

        // Small delay so users can see the success toast
        setTimeout(() => navigate('/'), 600);
      } else {
        toast.error(response.data?.message || 'Registration failed');
      }
    } catch (err) {
      // Backend validation errors format:
      // { success:false, message:"Validation failed", errors: { password:["..."], phone_number:["..."] } }
      const apiErrors = err.response?.data?.errors;
      const apiMessage = err.response?.data?.message;

      if (apiErrors && typeof apiErrors === 'object') {
        // Prioritize showing the first error nicely
        const firstField = Object.keys(apiErrors)[0];
        const firstMsg = apiErrors[firstField]?.[0] || 'Validation error';
        toast.error(firstMsg);
        setErrors((prev) => ({
          ...prev,
          // Map backend fields to our local ones for helperText rendering
          ...(apiErrors.full_name ? { full_name: apiErrors.full_name[0] } : {}),
          ...(apiErrors.email ? { email: apiErrors.email[0] } : {}),
          ...(apiErrors.password ? { password: apiErrors.password[0] } : {}),
          ...(apiErrors.password_confirmation ? { confirmPassword: apiErrors.password_confirmation[0] } : {}),
          ...(apiErrors.phone_number ? { phone: apiErrors.phone_number[0] } : {}),
          ...(apiErrors.country ? { country: apiErrors.country[0] } : {}),
        }));
      } else {
        toast.error(apiMessage || err.message || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <ToastContainer />
      <StyledCard>
        <Logo src={logo} alt="Logo" />
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            '& .MuiStack-root': { gap: 2 }
          }}
        >
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
              Create Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start managing your investment portfolio
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Full Name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                error={!!errors.full_name}
                helperText={errors.full_name}
                placeholder="e.g., Morad Soheim"
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="e.g., john.smith@example.com"
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />

              <Box>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  error={!!errors.password}
                  helperText={errors.password || "Use 8+ characters with mix of letters, numbers & symbols"}
                  placeholder="••••••••"
                  InputProps={{
                    startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  size="small"
                />
                <PasswordStrengthBar
                  variant="determinate"
                  value={
                    passwordStrength === 'weak' ? 33 :
                    passwordStrength === 'medium' ? 66 :
                    passwordStrength === 'strong' ? 100 : 0
                  }
                  strength={passwordStrength}
                />
              </Box>

              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                placeholder="••••••••"
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />

              <Select
                fullWidth
                name="country"
                value={formData.country}
                onChange={handleChange}
                error={!!errors.country}
                displayEmpty
                size="small"
              >
                <MenuItem value="" disabled>
                  Select your country
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
              {errors.country && <FormHelperText error>{errors.country}</FormHelperText>}

              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder={phonePlaceholders[formData.country] || "+1 (123) 456-7890"}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.primary">
                    I agree to the{' '}
                    <Link 
                      to="/terms" 
                      style={{ 
                        color: theme.palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link 
                      to="/privacy" 
                      style={{ 
                        color: theme.palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {errors.agreeToTerms && (
                <FormHelperText error>{errors.agreeToTerms}</FormHelperText>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLoading}
                sx={{
                  height: { xs: 40, sm: 48 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>

              <Typography align="center" color="text.primary">
                Already have an account?{' '}
                <Link 
                  to="/" 
                  style={{ 
                    color: theme.palette.primary.main,
                    textDecoration: 'none'
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Stack>
          </form>
        </CardContent>
      </StyledCard>
    </Container>
  );
};

export default Registered;
